import { MapPin, MessageCircle, Navigation, Phone, Instagram, Mail } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/lib/images";
import { useSeo } from "@/lib/seo";
import { track } from "@/lib/track";

const KARAD_ADDRESS = "Opp. SGM College,\nSGM College Road,\nSadichha Colony, Vidyanagar,\nSaidapur, Karad,\nMaharashtra 415124";
const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent("Opp. SGM College, SGM College Road, Sadichha Colony, Vidyanagar, Saidapur, Karad, Maharashtra 415124");

export default function Contact() {
  useSeo({
    title: "Contact | RIFIC — Say Hello",
    description:
      "Contact RIFIC — general enquiries, store visits and franchise conversations. rific.india@gmail.com, +91 72492 88910, or visit RIFIC Karad.",
    path: "/contact",
  });

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title={["SAY HELLO."]}
        copy="General enquiries, store visits and franchise conversations — one team, and we answer fast."
      />

      <section className="bg-cream pb-16 md:pb-24">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <div className="grid gap-5 md:grid-cols-2" data-testid="contact-cards">
            <Reveal>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-card"
                onClick={() => track("email_click", { placement: "contact", mailbox: "rific-india" })}
                className="block h-full rounded-3xl border border-cream-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(7,61,43,0.35)]"
              >
                <Mail className="h-6 w-6 text-orange-rific" />
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-deep/50">General Enquiries</p>
                <p className="mt-2 font-display text-xl font-bold tracking-[-0.01em] text-deep md:text-2xl">{CONTACT.email}</p>
              </a>
            </Reveal>

            <Reveal delay={0.07}>
              <div className="h-full rounded-3xl border border-cream-line bg-white p-8">
                <Phone className="h-6 w-6 text-orange-rific" />
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-deep/50">Call / WhatsApp</p>
                <p className="mt-2 font-display text-xl font-bold tracking-[-0.01em] text-deep md:text-2xl">{CONTACT.phoneDisplay}</p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="contact-call-button"
                    onClick={() => track("phone_click", { placement: "contact" })}
                    className="inline-flex items-center gap-1.5 rounded-full bg-deep px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-cream transition-colors hover:bg-orange-rific"
                  >
                    <Phone className="h-3.5 w-3.5" /> Call Us
                  </a>
                  <a
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-whatsapp-button"
                    onClick={() => track("whatsapp_click", { placement: "contact" })}
                    className="inline-flex items-center gap-1.5 rounded-full border border-deep/15 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-deep transition-colors hover:border-orange-rific hover:text-orange-rific"
                  >
                    <MessageCircle className="h-3.5 w-3.5" /> WhatsApp Us
                  </a>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.14}>
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-card"
                onClick={() => track("instagram_click", { placement: "contact" })}
                className="block h-full rounded-3xl border border-cream-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-28px_rgba(7,61,43,0.35)]"
              >
                <Instagram className="h-6 w-6 text-orange-rific" />
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-deep/50">Instagram</p>
                <p className="mt-2 font-display text-xl font-bold tracking-[-0.01em] text-deep md:text-2xl">{CONTACT.instagramHandle}</p>
              </a>
            </Reveal>

            <Reveal delay={0.21}>
              <div className="h-full rounded-3xl bg-deep p-8 text-cream">
                <MapPin className="h-6 w-6 text-orange-rific" />
                <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/55">Visit RIFIC Karad</p>
                <p className="mt-2 whitespace-pre-line font-body text-base leading-relaxed text-cream/90">{KARAD_ADDRESS}</p>
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-button"
                  onClick={() => track("directions_click", { store: "RIFIC – Karad", placement: "contact" })}
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-orange-rific px-5 py-3 text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-cream hover:text-deep"
                >
                  <Navigation className="h-3.5 w-3.5" /> Get Directions
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
